import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Calendar, MapPin, Link, X, CheckCircle } from 'lucide-react';
import { registrationAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';

const CourseRegistration = ({ course, onClose, onSuccess }) => {
  const { user, isAuthenticated } = useAuth();
  const [formData, setFormData] = useState({
    notes: '',
    contactPhone: user?.phone || '',
    contactEmail: user?.email || ''
  });
  const [registering, setRegistering] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!isAuthenticated) {
      toast.error('Please login to register for this course');
      return;
    }

    setRegistering(true);
    try {
      await registrationAPI.register({
        courseId: course.id,
        notes: formData.notes,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail
      });
      
      toast.success('Successfully registered for the course!');
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Failed to register for course';
      toast.error(message);
    } finally {
      setRegistering(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
      >
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
              Register for Course
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Course Info */}
          <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2">
              {course.title}
            </h4>
            <div className="space-y-2 text-sm text-gray-600 dark:text-gray-300">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4" />
                <span>{course.duration} hours</span>
              </div>
              <div className="flex items-center space-x-2">
                {course.courseType === 'onsite' ? (
                  <>
                    <MapPin className="w-4 h-4" />
                    <span>{course.location || 'Location TBD'}</span>
                  </>
                ) : (
                  <>
                    <Link className="w-4 h-4" />
                    <span>Online Course</span>
                  </>
                )}
              </div>
              <div className="flex items-center space-x-2">
                <span className="capitalize">{course.level} Level</span>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contact Phone
              </label>
              <input
                type="tel"
                value={formData.contactPhone}
                onChange={(e) => setFormData({...formData, contactPhone: e.target.value})}
                className="input w-full"
                placeholder="Your phone number"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Contact Email
              </label>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({...formData, contactEmail: e.target.value})}
                className="input w-full"
                placeholder="Your email address"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Additional Notes (Optional)
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="textarea w-full"
                rows={3}
                placeholder="Any special requirements or questions..."
              />
            </div>

            <div className="flex items-center space-x-2 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-outline flex-1"
                disabled={registering}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary flex-1 inline-flex items-center justify-center"
                disabled={registering}
              >
                {registering ? (
                  <>
                    <div className="loading-spinner w-4 h-4 mr-2"></div>
                    Registering...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Register Now
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </motion.div>
    </div>
  );
};

export default CourseRegistration;

